import { PButton } from "@porsche-design-system/components-react"
import type {
	InventoryInformationChange,
	InventoryInformationEditView,
	InventoryInformationView,
	ModelInformationView,
	VisibilityView,
} from "@slfinrtl/ui-bff-routes"
import { updateInventoryInformationRoute, updatePricingRoute, Visibility } from "@slfinrtl/ui-bff-routes"
import { dequal } from "dequal"
import { useCallback, useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { getFeatureToggle } from "src/common/getConfig"
import { getDifference } from "src/common/getDifference/getDifference"
import { transformValues } from "src/common/transformValues/transformValues"
import type { InventoryInformationFormValues } from "src/components"
import {
	ButtonGroup,
	conditionFieldNames,
	getInventoryInformationChangeRules,
	mapToInventoryInformationChange,
	usePricingForm,
} from "src/components"
import { PricingFormContainer } from "src/containers/inventoryInformation/pricingForm/PricingFormContainer"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import { useBffDynamic, useConfig, useCurrencyLimit } from "src/hooks"
import type { PriceConfirmationModalProps } from "src/modals/priceConfirmationModal/PriceConfirmationModal"
import { PriceConfirmationModal } from "src/modals/priceConfirmationModal/PriceConfirmationModal"
import { priceConfirmationModalMessages } from "src/modals/priceConfirmationModal/priceConfirmationModal.messages"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"

interface PricingDataFormContainerProps {
	inventoryId: string
	onCancel: () => void
	onSaved: (view: InventoryInformationView) => void
	view: InventoryInformationEditView
}

export const PricingDataFormContainer = withErrorHandler(
	({ inventoryId, onCancel, onSaved, view }: PricingDataFormContainerProps) => {
		const { handleSubmit, reset, watch } = useFormContext<InventoryInformationFormValues>()

		const isInventoryPriceServiceEnabled = getFeatureToggle("inventory-price-service-enabled")

		const [isSaving, setIsSaving] = useState(false)
		const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()
		const [visibilityView, setVisibilityView] = useState<VisibilityView>()

		const { subscribe } = useVehicleDetailsContext()
		const showModal = useModal<PriceConfirmationModalProps>(PriceConfirmationModal)
		const { isPriceBelowCurrencyLimit } = useCurrencyLimit()

		useEffect(() => {
			subscribe("modelInformationChanged", setModelInformationView)
			subscribe("visibilityChanged", setVisibilityView)
		}, [subscribe])

		const config = useConfig()

		const values: InventoryInformationFormValues = watch()
		const pricingFormProps = usePricingForm({
			availableAccessories: view.availableAccessories,
			formattedMsrp: view.formattedMsrp,
			inventoryId,
			isDraft: false,
			modelSeriesKey: view.modelSeriesKey,
			priceHistory: view.priceHistory,
			values,
		})

		useEffect(() => {
			const transformedValues = transformValues(getInventoryInformationChangeRules(config), values, config)

			// Reset only listing values. Leave other stages as they are for now, to make sure that changing a value and then
			// reverting it leads back to the original state.
			const newValues: InventoryInformationFormValues = { ...values }
			Object.values(conditionFieldNames).forEach((fieldName) => {
				;(newValues[fieldName] as unknown) = transformedValues[fieldName]
			})

			if (!dequal(newValues, values)) {
				reset(newValues, { keepErrors: true, keepFieldsRef: true })
			}
		}, [reset, config, values])

		const inventoryPatch = useBffDynamic(updateInventoryInformationRoute, "inline")
		const inventoryPricePatch = useBffDynamic(updatePricingRoute, "none")

		const onValid = () => {
			const transformedValues = transformValues<InventoryInformationFormValues>(
				getInventoryInformationChangeRules(config),
				values,
				config,
			)
			const informationChange = mapToInventoryInformationChange(transformedValues)
			if (!informationChange) {
				return
			}

			const price =
				informationChange.pricing?.pricingUseCase === "CANADA_PRICE_BREAKDOWN" ||
				informationChange.pricing?.pricingUseCase === "JAPAN_VEHICLE_AND_MISC" ||
				informationChange.pricing?.pricingUseCase === "EXTRA_COSTS_PRICE_BREAKDOWN"
					? informationChange.pricing.vehiclePrice
					: informationChange.pricing?.price
			if (
				price &&
				modelInformationView &&
				isPriceBelowCurrencyLimit(price, modelInformationView.modelYear) &&
				(visibilityView?.visibility.visibility === Visibility.VISIBLE ||
					visibilityView?.visibility.visibility === Visibility.INVALID)
			) {
				showModal({
					currentPrice: price,
					headline: priceConfirmationModalMessages.vehiclePriceLowHeadline,
					label: priceConfirmationModalMessages.vehiclePriceLowLabel,
					onConfirmClicked: () => {
						save(informationChange)
					},
				})
			} else {
				save(informationChange)
			}
		}

		const save = useCallback(
			(informationChange: InventoryInformationChange) => {
				const patchedInformation = informationChange ? getDifference(informationChange, view.values) : undefined

				setIsSaving(true)

				const inventoryPatchPromise = inventoryPatch({
					body: patchedInformation
						? {
								...patchedInformation,
								pricing: {
									...patchedInformation.pricing,
									// PricingUseCase must always be sent to the
									// bff
									pricingUseCase: view.values.pricing.pricingUseCase,
								},
							}
						: {},
					pathParams: { inventoryId },
					queryParams: {},
				})

				const inventoryPricePatchPromise =
					isInventoryPriceServiceEnabled &&
					view.values.pricing.pricingUseCase === "EXTRA_COSTS_PRICE_BREAKDOWN"
						? inventoryPricePatch({
								body: patchedInformation
									? {
											...patchedInformation.pricing,
											// PricingUseCase must always be sent to the
											// bff
											pricingUseCase: view.values.pricing.pricingUseCase,
										}
									: {
											pricingUseCase: view.values.pricing.pricingUseCase,
										},
								pathParams: { inventoryId },
								queryParams: {},
							}).catch((_) => {
								return null
							})
						: Promise.resolve(null)

				return Promise.all([inventoryPatchPromise, inventoryPricePatchPromise])
					.then(([inventoryPatchResult, _]) => {
						onSaved(inventoryPatchResult)
					})
					.finally(() => setIsSaving(false))
			},
			[inventoryId, onSaved, inventoryPatch, inventoryPricePatch, isInventoryPriceServiceEnabled, view.values],
		)

		return (
			<>
				<ErrorNotificationInline />
				<PricingFormContainer {...pricingFormProps} />
				<ButtonGroup>
					<PButton loading={isSaving} onClick={handleSubmit(onValid)}>
						<FormattedMessage {...formMessages.save} />
					</PButton>
					<PButton type="button" variant="secondary" onClick={onCancel}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</>
		)
	},
)
